<template>
  <header class="app-header" role="banner">
    <router-link :to="{ name: 'dashboard' }" class="app-header__brand">
      <span class="app-header__brand-mark">F</span>
      <span class="app-header__brand-text">
        <b>Fiscozen</b>
        <span>Contracts</span>
      </span>
    </router-link>

    <nav class="app-header__nav" :aria-label="$t('nav.main_menu')">
      <router-link
        v-for="item in visibleItems"
        :key="item.routeName"
        :to="{ name: item.routeName }"
        class="app-header__nav-link"
      >
        {{ $t(item.label) }}
      </router-link>
    </nav>

    <div class="app-header__spacer" />

    <div v-if="currentUser" class="app-header__role">
      <div class="app-header__role-meta">
        <span class="app-header__role-name">{{ userDisplayName }}</span>
        <span v-if="userRoleLabel" class="app-header__role-tag">{{ userRoleLabel }}</span>
      </div>
      <router-link
        :to="{ name: 'account' }"
        class="app-header__account"
        :aria-label="$t('nav.account')"
        :title="$t('nav.account')"
      >
        <Avatar
          :first-name="currentUser.first_name"
          :last-name="currentUser.last_name"
          :picture-url="currentUser.picture_url"
          size="md"
        />
      </router-link>
      <button
        type="button"
        class="app-header__logout"
        :aria-label="$t('auth.logout')"
        :title="$t('auth.logout')"
        @click="$emit('logout')"
      >
        <ArrowRightStartOnRectangleIcon class="app-header__logout-icon" />
      </button>
    </div>
  </header>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { ArrowRightStartOnRectangleIcon } from '@heroicons/vue/24/outline'
import Avatar from '@/components/atoms/Avatar.vue'
import { navigationGroups } from '@/config/navigation'
import type { AccessLevel, AuthUser } from '@/types/auth.types'
import type { NavItem } from '@/types/navigation.types'

const props = defineProps<{
  currentUser: AuthUser | null
}>()

defineEmits<{
  logout: []
}>()

const visibleItems = computed<NavItem[]>(() => {
  const accessLevel: AccessLevel = props.currentUser?.access_level ?? 'access_level.user'
  return navigationGroups
    .filter((group) => group.access_levels.includes(accessLevel))
    .flatMap((group) => group.items.filter((item) => item.access_levels.includes(accessLevel)))
})

const userDisplayName = computed(() => {
  if (!props.currentUser) return ''
  return `${props.currentUser.first_name} ${props.currentUser.last_name}`.trim()
})

const userRoleLabel = computed(() => props.currentUser?.job_title ?? '')
</script>

<style scoped lang="scss">
.app-header {
  position: sticky;
  top: 0;
  height: 48px;
  display: flex;
  align-items: stretch;
  background: var(--app-sidebar-bg);
  color: var(--app-sidebar-text, #ffffff);
  z-index: 100;
  border-bottom: 1px solid var(--app-sidebar-divider, rgba(255, 255, 255, 0.2));
}

.app-header__brand {
  display: flex;
  align-items: center;
  gap: var(--space-2, 8px);
  padding: 0 var(--space-4, 16px);
  border-right: 1px solid var(--app-sidebar-divider, rgba(255, 255, 255, 0.2));
  color: var(--app-sidebar-text, #ffffff);
  text-decoration: none;
  font-size: var(--text-size-base);
  letter-spacing: var(--letter-spacing-label);
}

.app-header__brand-mark {
  width: 20px;
  height: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--app-sidebar-text, #ffffff);
  color: var(--app-sidebar-bg);
  font-size: var(--text-size-sm);
  font-weight: var(--font-weight-semibold);
  flex-shrink: 0;
}

.app-header__brand-text {
  display: inline-flex;
  align-items: baseline;
  gap: 4px;
}

.app-header__brand-text b {
  font-weight: var(--font-weight-semibold);
}

.app-header__brand-text span {
  font-weight: 300;
  opacity: 0.85;
}

.app-header__nav {
  display: flex;
  align-items: stretch;
}

.app-header__nav-link {
  display: inline-flex;
  align-items: center;
  padding: 0 var(--space-4, 16px);
  color: rgba(255, 255, 255, 0.85);
  text-decoration: none;
  font-size: var(--text-size-base);
  letter-spacing: var(--letter-spacing-label);
  transition:
    background 70ms ease,
    color 70ms ease;

  &:hover {
    background: var(--app-sidebar-hover, rgba(255, 255, 255, 0.12));
    color: var(--app-sidebar-text, #ffffff);
  }
}

.app-header__nav-link.router-link-active {
  background: var(--app-sidebar-active, rgba(255, 255, 255, 0.25));
  color: var(--app-sidebar-text, #ffffff);
  font-weight: var(--font-weight-semibold);
}

.app-header__spacer {
  flex: 1;
}

.app-header__role {
  display: flex;
  align-items: center;
  gap: var(--space-3, 12px);
  padding: 0 var(--space-4, 16px);
  border-left: 1px solid var(--app-sidebar-divider, rgba(255, 255, 255, 0.2));
}

.app-header__role-meta {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 1px;
  min-width: 0;
}

.app-header__role-name {
  font-size: 13px;
  color: var(--app-sidebar-text, #ffffff);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.app-header__role-tag {
  font-size: 11px;
  letter-spacing: var(--letter-spacing-label);
  color: rgba(255, 255, 255, 0.7);
  white-space: nowrap;
}

.app-header__account {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  cursor: pointer;
  text-decoration: none;
  transition: box-shadow 70ms ease;

  &:hover {
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.4);
  }

  &:focus-visible {
    outline: 2px solid #ffffff;
    outline-offset: 2px;
  }
}

.app-header__logout {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  border: 0;
  color: rgba(255, 255, 255, 0.85);
  cursor: pointer;
  transition:
    background 70ms ease,
    color 70ms ease;

  &:hover {
    background: var(--app-sidebar-hover, rgba(255, 255, 255, 0.12));
    color: var(--app-sidebar-text, #ffffff);
  }
}

.app-header__logout-icon {
  width: 18px;
  height: 18px;
}

@media (max-width: 720px) {
  .app-header__nav {
    overflow-x: auto;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  .app-header__role-tag {
    display: none;
  }
}
</style>
